import { Injectable, OnDestroy, inject } from '@angular/core';
import { getComponent, Visualize3dObject } from '../models/visualize3d-object';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { QModelComponent } from '../v3d-components/q-model-component';
import { ComponentHandlerProvider } from '../v3d-components/handlers/component-handler-provider';
import { instanceOfOnObject3dInit } from '../view-interactions/object-3d-events';
import { ApiService } from './api.service';
import { setLinearColorRecursively, storeOriginalMaterialRecursively } from '../utility/viewer/material-utility';
import { BufferGeometry, LineSegments, Mesh, Object3D } from 'three';
import { QmdJsonToThreejsConverterService } from './qmd-json-to-threejs-converter.service';
import { isLineSegments, isMesh } from '../../3rd-party/types/is-threejs-type';
import { ThreeJsMaterialLikeForMesh } from '../../3rd-party/types/three-js-material-like';
import { ThreeJsMeshLike } from '../../3rd-party/types/three-js-mesh-like';
import { Visualize3dUserData } from '../models/visualize3d-user-data';

@Injectable({
  providedIn: 'root',
})
export class QmodelThreejsCreatorService implements OnDestroy {
  private apiService = inject(ApiService);
  private componentHandlerProvider = inject(ComponentHandlerProvider);
  private qmdJsonToThreeJsConverterService = inject(QmdJsonToThreejsConverterService);

  public error = new Subject<string>();

  ngOnDestroy(): void {
    this.error.unsubscribe();
  }

  public createThreeJsObjectHierarchy(v3dObject: Visualize3dObject, parentObject3d: Object3D): Observable<Object3D> {
    const observable = this.loadFromQmdJson(v3dObject, parentObject3d);

    if (v3dObject.children.length === 0) {
      return observable;
    }
    return observable.pipe(
      switchMap(object3d => {
        return forkJoin(v3dObject.children.map(v3dChild => this.createThreeJsObjectHierarchy(v3dChild, object3d))).pipe(
          map(() => {
            return object3d;
          }),
        );
      }),
    );
  }

  public loadFromQmdJson(v3dObject: Visualize3dObject, parentObject3d: Object3D): Observable<Object3D> {
    const qmodel = getComponent<QModelComponent>(v3dObject, 'QModel');
    if (!qmodel) {
      const object3d = new Object3D();
      this.initializeThreeJsObject(object3d, v3dObject, parentObject3d);
      return of(object3d);
    }

    return this.apiService.exportQmdJson(qmodel).pipe(
      map(qmdJsonData => {
        const object3d = this.qmdJsonToThreeJsConverterService.convertToObject3d(qmdJsonData);
        this.initializeThreeJsObject(object3d, v3dObject, parentObject3d);
        return object3d;
      }),
      catchError(error => {
        const object3d = new Object3D();
        this.initializeThreeJsObject(object3d, v3dObject, parentObject3d);
        const msg = `Loading THREE.Object3D for object ${v3dObject.name} [${v3dObject.id}] failed. Couldn't fetch QModel with id ${
          qmodel.id
        }]. ${JSON.stringify(error)}`;
        console.error(msg);
        this.error.next(msg);
        return of(object3d);
      }),
    );
  }

  public initializeThreeJsObject(object3d: Object3D, v3dObject: Visualize3dObject, parentObject3d: Object3D): void {
    setLinearColorRecursively(object3d);
    storeOriginalMaterialRecursively(object3d);
    object3d.name = `${v3dObject.name} ${v3dObject.id}`;
    const userData: Visualize3dUserData = {
      v3dObject,
      objectsWithMaterials: new Array<ThreeJsMeshLike>(),
      objects: new Array<Object3D>(),
    };
    object3d.userData = userData;

    object3d.traverse(o => {
      this.tryAddObjectToObjectsWithMaterials(o, object3d.userData as Visualize3dUserData);
    });

    if (parentObject3d) {
      parentObject3d.add(object3d);
    }
    this.tryInvokeOnInitComponentHandlers(object3d, v3dObject);
  }

  private tryAddObjectToObjectsWithMaterials(object3d: Object3D, userData: Visualize3dUserData): void {
    userData.objects.push(object3d);
    if (isMesh(object3d)) {
      userData.objectsWithMaterials.push(object3d as Mesh<BufferGeometry, ThreeJsMaterialLikeForMesh>);
    } else if (isLineSegments(object3d)) {
      userData.objectsWithMaterials.push(object3d as LineSegments<BufferGeometry, ThreeJsMaterialLikeForMesh>);
    }
  }

  private tryInvokeOnInitComponentHandlers(object3d: Object3D, v3dObject: Visualize3dObject): void {
    for (const component of v3dObject.components) {
      const handler = this.componentHandlerProvider.getHandler(component.type);
      if (handler && instanceOfOnObject3dInit(handler)) {
        handler.initialize(object3d, v3dObject, component);
      }
    }
  }
}
